import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, User, LogOut, ChevronDown, Star, Home as HomeIcon, Sparkles, BookOpen, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const DropdownMenu = ({ label, items, isHindi }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[#F5F2EB] hover:text-[#C9A84C] transition-colors text-sm uppercase tracking-wider"
        data-testid={`dropdown-${label.toLowerCase().replace(/\s/g, '-')}`}>
        {label} <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
            className="absolute top-full left-0 mt-2 bg-[#1A1A1A] border border-[#C9A84C]/20 min-w-[200px] shadow-xl z-50 backdrop-blur-xl">
            {items.map((item, i) => (
              <Link key={i} to={item.to} onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-5 py-3 text-[#F5F2EB] hover:text-[#C9A84C] hover:bg-[#C9A84C]/5 transition-colors text-sm border-b border-[#C9A84C]/5 last:border-0"
                data-testid={`dropdown-item-${item.to.replace('/', '')}`}>
                {item.icon && <item.icon className="w-4 h-4 text-[#C9A84C]" />}
                <span>{isHindi ? item.labelHi : item.labelEn}</span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileFeaturesOpen, setMobileFeaturesOpen] = useState(false);
  const isHindi = i18n.language === 'hi';

  const handleLogout = async () => { await logout(); navigate('/'); };

  const serviceItems = [
    { to: '/astrology', labelEn: 'Astrology Services', labelHi: 'ज्योतिष सेवाएं', icon: Star },
    { to: '/vastu', labelEn: 'Vastu Services', labelHi: 'वास्तु सेवाएं', icon: HomeIcon },
    { to: '/booking', labelEn: 'Book Consultation', labelHi: 'परामर्श बुक करें', icon: Calendar }
  ];

  const freeFeatureItems = [
    { to: '/horoscope', labelEn: 'Daily Horoscope', labelHi: 'दैनिक राशिफल', icon: Sparkles },
    { to: '/kundli', labelEn: 'Free Kundli', labelHi: 'मुफ्त कुंडली', icon: BookOpen },
    { to: '/panchang', labelEn: 'Panchang', labelHi: 'पंचांग', icon: Calendar }
  ];

  return (
    <header className="fixed w-full z-50 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-[#C9A84C]/10 transition-all duration-300">
      <div className="px-6 md:px-12 lg:px-24 py-3">
        <div className="flex items-center justify-between">
          {/* Logo only - no text */}
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src="/images/L1.png" alt="Astrology Times" className="h-11 w-11 object-contain" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7">
            <Link to="/" className="text-[#F5F2EB] hover:text-[#C9A84C] transition-colors text-sm uppercase tracking-wider">
              {t('home')}
            </Link>
            <DropdownMenu label={isHindi ? 'सेवाएं' : 'Services'} items={serviceItems} isHindi={isHindi} />
            <DropdownMenu label={isHindi ? 'निःशुल्क' : 'Free Features'} items={freeFeatureItems} isHindi={isHindi} />
            <Link to="/blog" className="text-[#F5F2EB] hover:text-[#C9A84C] transition-colors text-sm uppercase tracking-wider">{t('blog')}</Link>
            <Link to="/about" className="text-[#F5F2EB] hover:text-[#C9A84C] transition-colors text-sm uppercase tracking-wider">{t('about')}</Link>
            <Link to="/contact" className="text-[#F5F2EB] hover:text-[#C9A84C] transition-colors text-sm uppercase tracking-wider">{t('contact')}</Link>
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated && user ? (
              <>
                <Link to="/dashboard" className="flex items-center gap-2 text-[#F5F2EB] hover:text-[#C9A84C] transition-colors text-sm uppercase tracking-wider" data-testid="dashboard-link">
                  <User className="w-4 h-4" /> {user.name}
                </Link>
                <button onClick={handleLogout} className="flex items-center gap-2 text-[#F5F2EB] hover:text-[#C9A84C] transition-colors text-sm uppercase tracking-wider" data-testid="logout-button">
                  <LogOut className="w-4 h-4" /> {t('logout')}
                </button>
              </>
            ) : (
              <Link to="/login" className="bg-[#C9A84C] text-[#0A0A0A] font-medium px-6 py-2 hover:bg-[#E5C870] transition-colors text-sm uppercase tracking-widest" data-testid="login-button">
                {t('login')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-[#C9A84C]" data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#1A1A1A] border-t border-[#C9A84C]/10">
            <nav className="flex flex-col px-6 py-4 gap-3">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-[#F5F2EB] hover:text-[#C9A84C] text-sm uppercase tracking-wider">{t('home')}</Link>

              {/* Services Accordion */}
              <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className="flex items-center justify-between text-[#F5F2EB] hover:text-[#C9A84C] text-sm uppercase tracking-wider">
                {isHindi ? 'सेवाएं' : 'Services'} <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 flex flex-col gap-2">
                  {serviceItems.map((item, i) => (
                    <Link key={i} to={item.to} onClick={() => setMobileMenuOpen(false)} className="text-[#A3A3A3] hover:text-[#C9A84C] text-sm">{isHindi ? item.labelHi : item.labelEn}</Link>
                  ))}
                </div>
              )}

              {/* Free Features Accordion */}
              <button onClick={() => setMobileFeaturesOpen(!mobileFeaturesOpen)} className="flex items-center justify-between text-[#F5F2EB] hover:text-[#C9A84C] text-sm uppercase tracking-wider">
                {isHindi ? 'निःशुल्क सुविधाएं' : 'Free Features'} <ChevronDown className={`w-4 h-4 transition-transform ${mobileFeaturesOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileFeaturesOpen && (
                <div className="pl-4 flex flex-col gap-2">
                  {freeFeatureItems.map((item, i) => (
                    <Link key={i} to={item.to} onClick={() => setMobileMenuOpen(false)} className="text-[#A3A3A3] hover:text-[#C9A84C] text-sm">{isHindi ? item.labelHi : item.labelEn}</Link>
                  ))}
                </div>
              )}

              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="text-[#F5F2EB] hover:text-[#C9A84C] text-sm uppercase tracking-wider">{t('blog')}</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="text-[#F5F2EB] hover:text-[#C9A84C] text-sm uppercase tracking-wider">{t('about')}</Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="text-[#F5F2EB] hover:text-[#C9A84C] text-sm uppercase tracking-wider">{t('contact')}</Link>

              {isAuthenticated && user ? (
                <>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="text-[#C9A84C] hover:text-[#E5C870] text-sm uppercase tracking-wider">{t('myAccount')}</Link>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="text-left text-[#F5F2EB] hover:text-[#C9A84C] text-sm uppercase tracking-wider">{t('logout')}</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="text-[#C9A84C] hover:text-[#E5C870] text-sm uppercase tracking-wider">{t('login')}</Link>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
